import React, { useMemo } from "react";
import { Check, Loader2, ChevronDown } from "lucide-react";

/**
 * Workflow chain visualization.
 *
 * The backend (deep_agents/workflow_executor.py) streams every assistant in a
 * workflow chain into a SINGLE assistant message, delimiting each step with a
 * markdown heading of the form:
 *
 *     ## 🤖 Assistant 1 · <assistant name>
 *
 * Non-final assistants are emitted in full once they finish; the final
 * assistant streams live under its heading. Any MCP tool calls are hoisted to
 * the top of the message (MessageBox) into the "Gathering information" pill.
 *
 * This component splits that single message on those headings and renders each
 * assistant as its own segment — a step badge, the assistant name, a
 * running/done status, the answer, and a divider line below — so the chain
 * reads as a step-by-step pipeline instead of stacked H2 headings.
 *
 * Each segment's body is rendered with MessageBox's own markdown renderer
 * (passed in as `renderMarkdown`) so tool blocks, code, links, tables, etc.
 * render exactly as they do elsewhere.
 */

// Matches the exact heading the workflow executor emits:
//   "## 🤖 Assistant 1 · Name"
// The 🤖 emoji is REQUIRED so this never misfires on ordinary messages that
// merely happen to contain a heading like "## Assistant notes". Separator is
// lenient (middle dot / bullet variants) purely for resilience.
const HEADING_SOURCE =
  "^[ \\t]*#{2,3}\\s+🤖\\s*Assistant\\s+(\\d+)\\s*[·•∙‧]\\s*(.+?)[ \\t]*$";

/** True when the content is a workflow chain message (has ≥1 assistant heading). */
export function isWorkflowContent(text: string): boolean {
  if (!text) return false;
  return new RegExp(HEADING_SOURCE, "m").test(text);
}

interface ParsedSection {
  step: string;
  name: string;
  body: string;
}

interface Parsed {
  preamble: string;
  sections: ParsedSection[];
}

function parseSections(content: string): Parsed | null {
  const re = new RegExp(HEADING_SOURCE, "gm");
  const matches: { index: number; length: number; step: string; name: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    matches.push({ index: m.index, length: m[0].length, step: m[1], name: m[2] });
  }
  if (matches.length === 0) return null;

  const preamble = content.slice(0, matches[0].index).trim();
  const sections: ParsedSection[] = matches.map((cur, i) => {
    const bodyStart = cur.index + cur.length;
    const bodyEnd = i + 1 < matches.length ? matches[i + 1].index : content.length;
    return { step: cur.step, name: cur.name, body: content.slice(bodyStart, bodyEnd).trim() };
  });

  return { preamble, sections };
}

interface WorkflowPipelineProps {
  content: string;
  /** True while the run is still streaming. */
  loading: boolean;
  /** True when this is the last message in the chat (i.e. the live one). */
  isLast: boolean;
  /** MessageBox's markdown renderer, reused for parity with normal messages. */
  renderMarkdown: (text: string) => React.ReactNode;
}

const WorkflowPipeline: React.FC<WorkflowPipelineProps> = ({
  content,
  loading,
  isLast,
  renderMarkdown,
}) => {
  const parsed = useMemo(() => parseSections(content), [content]);

  // Not actually a workflow message — fall back to plain rendering.
  if (!parsed) return <>{renderMarkdown(content)}</>;

  const { preamble, sections } = parsed;
  const lastIdx = sections.length - 1;
  const streaming = loading && isLast;

  return (
    <div className="flex flex-col w-full">
      {/* Hoisted tool blocks ("Gathering information" pill) live here. */}
      {preamble && <div className="mb-3">{renderMarkdown(preamble)}</div>}

      {sections.map((sec, i) => {
        // Only the final section streams live; everything before it is done.
        const isRunning = i === lastIdx && streaming;
        const isLastRow = i === lastIdx;

        return (
          // items-stretch lets the rail column match the card height so the
          // vertical cord can run the full length between consecutive nodes.
          <div key={`wf-step-${i}`} className="flex gap-3 sm:gap-4 items-stretch">
            {/* LEFT RAIL — numbered node + the vertical cord connecting steps */}
            <div className="relative flex flex-col items-center w-7 shrink-0">
              <div
                className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full border-2 text-xs font-semibold ${
                  isRunning
                    ? "bg-surface-overlay border-violet-500 text-violet-600"
                    : "bg-violet-500 border-violet-500 text-white"
                }`}
              >
                {sec.step}
                {isRunning && (
                  <span className="absolute -inset-1 rounded-full border-2 border-violet-400/60 animate-ping" />
                )}
              </div>
              {/* Little horizontal connector from the node into its card. */}
              <div className="absolute left-full top-3.5 -translate-y-1/2 h-0.5 w-3 sm:w-4 bg-gray-200 rounded-full" />
              {/* Cord + downward flow arrow to the next step (omitted on last). */}
              {!isLastRow && (
                <div className="flex-1 flex flex-col items-center mt-1">
                  <div className="w-0.5 flex-1 bg-gray-200 rounded-full" />
                  <ChevronDown
                    size={14}
                    strokeWidth={2.5}
                    className="-mt-0.5 text-gray-400"
                  />
                </div>
              )}
            </div>

            {/* RIGHT — the assistant's answer card */}
            <div className="flex-1 min-w-0 pb-6">
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                {/* Step header: "ASSISTANT N" eyebrow over the assistant name. */}
                <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100">
                  <div className="flex flex-col min-w-0 leading-tight">
                    <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-violet-500">
                      Assistant {sec.step}
                    </span>
                    <span className="font-semibold text-gray-800 text-sm truncate">
                      {sec.name}
                    </span>
                  </div>
                  <span className="ml-auto flex items-center gap-1.5 text-xs shrink-0">
                    {isRunning ? (
                      <>
                        <Loader2 size={13} className="animate-spin text-blue-600" />
                        <span className="text-blue-600 font-medium">Running</span>
                      </>
                    ) : (
                      <>
                        <Check size={13} className="text-green-600" />
                        <span className="text-green-600 font-medium">Done</span>
                      </>
                    )}
                  </span>
                </div>

                {/* Step answer */}
                <div className="px-4 py-3">
                  {sec.body ? (
                    renderMarkdown(sec.body)
                  ) : (
                    <span className="text-gray-400 text-sm italic">…</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowPipeline;
